import { merge, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'
import size from 'lodash/size'
import { OrderResultsReducerChangeLastOrderAction, OrderResultsReducerNewOrderPendingAction } from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { OrderFailedResult, OrderResultWithPendingStatus, OrderSuccessResultWithMeta } from 'components/features/rewards/rewardsPane/types/orderResult'
import { EpicRefined } from 'interfaces/middleware'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import { CryptoRewardsPaneActions } from 'components/features/rewards/rewardsPane/store/actions'
import { byType } from 'rxOperators/byType'
import { FLOATING_PANE_AUTO_INVESTMENTS_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { POST_CHANGE_AUTOINVESTMENTS_IN_CRYPTO_MODE_URL } from 'components/features/rewards/rewardsPane/constants/endpoints'
import { ModifyFloatingPane } from 'actions/floatingPanesActions'
import { CryptoRewardsChangeAutoinvestmentsModeRequest } from 'components/features/rewards/rewardsPane/types/apiRequests'
import { CryptoRewardsAutoInvestmentsModeStateChangeFailedResponse } from 'components/features/rewards/rewardsPane/types/apiResponses'
import { CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR } from 'components/features/rewards/rewardsPane/constants/defaults'
import BountyActions, { SetRewardsCryptoInvestmentsStateAction, SetRewardsCryptoInvestmentsStateActionPayload } from 'actions/bountyActions'
import { AutoinvestmentsModeDisableAction } from 'components/features/rewards/rewardsPane/store/actions/autoInvestmentsMode'
import { getAutoinvestCurrencyIdOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/cryptoRewards'

type PaneAutoinvestmentsMode = typeof FLOATING_PANE_AUTO_INVESTMENTS_MODE

export const disableAutoinvestmentsModeEpic: EpicRefined<
  | CryptoRewardsPaneActions
  | ModifyFloatingPane
  | BountyActions
  | AjaxCatchExtraActions
> = (
  (action$, store, { post }) => action$
    .pipe(
      byType(AutoinvestmentsModeDisableAction.type),
      mergeMap(
        () => {
          const actionResetLastOrderResultObservable = of(
            new OrderResultsReducerNewOrderPendingAction(FLOATING_PANE_AUTO_INVESTMENTS_MODE)
          )
          const cryptoCurrencyIDChosenForInvestment = getAutoinvestCurrencyIdOrNullSelector(store.getState())
          const sendOrderParams: CryptoRewardsChangeAutoinvestmentsModeRequest = {
            is_autoinvest_on: false,
            ...!cryptoCurrencyIDChosenForInvestment ? {} : { autoinvest_ticker: cryptoCurrencyIDChosenForInvestment },
          }
          const sendOrderObservable = post(POST_CHANGE_AUTOINVESTMENTS_IN_CRYPTO_MODE_URL, {
            body: sendOrderParams,
          }).pipe(
            mergeMap(() => {
              const orderSuccessResult: OrderSuccessResultWithMeta<PaneAutoinvestmentsMode> = {
                mode: FLOATING_PANE_AUTO_INVESTMENTS_MODE,
                currencyId: cryptoCurrencyIDChosenForInvestment,
              }
              const orderResultWithStatus: OrderResultWithPendingStatus<PaneAutoinvestmentsMode> = {
                mode: FLOATING_PANE_AUTO_INVESTMENTS_MODE,
                isPending: false,
                success: orderSuccessResult,
                errors: undefined,
              }
              const rewardsAccountUpdatedState: SetRewardsCryptoInvestmentsStateActionPayload = {
                is_autoinvest_on: false,
                autoinvest_ticker: null,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(orderResultWithStatus)
              const actionChangeUserRewardsState = new SetRewardsCryptoInvestmentsStateAction(rewardsAccountUpdatedState)
              return merge(
                of(actionSetOrderResult),
                of(actionChangeUserRewardsState),
              )
            }),
            ajaxCatch((error) => {
              const {
                autoinvest_ticker,
                is_autoinvest_on,
                non_field_errors,
              }: CryptoRewardsAutoInvestmentsModeStateChangeFailedResponse = Boolean(size(error.response))
                ? error.response
                : CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR
              const fieldsErrors = {
                autoinvest_ticker,
                is_autoinvest_on,
              }
              const requestFailedErrors: OrderFailedResult<PaneAutoinvestmentsMode> = {
                fieldsErrors: fieldsErrors,
                generalErrors: non_field_errors,
              }
              const enableAutoInvestmentsModeWithStatus: OrderResultWithPendingStatus<PaneAutoinvestmentsMode> = {
                mode: FLOATING_PANE_AUTO_INVESTMENTS_MODE,
                isPending: false,
                errors: requestFailedErrors,
                success: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(enableAutoInvestmentsModeWithStatus)
              return of(actionSetOrderResult)
            })
          )
          return merge(
            actionResetLastOrderResultObservable,
            sendOrderObservable
          )
        }
      )
    )
)
